import { isEqual } from 'lodash-es'
import { $pegApi } from '@/boot/peg-api'
import { $analytics } from '@/boot/analytics'
import { $rollbar } from '@/boot/rollbar'
import { enrichAuctionItem } from '@/utils/product.util'
import { isValidAddress } from '@/utils/validator.util'
import { removeUnusedFields } from '@/utils/form.util'
import { PICKUP, DELIVERY } from '@/constants/fulfillment.const'

export const useCheckoutStore = defineStore('checkout', () => {
  const checkout = ref({})
  const billingSameAsShipping = ref(true)
  const isUpdating = ref(false)

  const hasError = computed(() => 'success' in checkout.value && !checkout.value.success)
  const hasDeliveryFulfillment = computed(() => (checkout.value.items || []).some(item => item.isDeliveryFulfillment))
  const hasPickupFulfillment = computed(() => (checkout.value.items || []).some(item => item.isPickupFulfillment))

  const shippingAddressIsComplete = computed(() => isValidAddress(checkout.value.shippingAddress))
  const billingAddressIsComplete = computed(() => billingSameAsShipping.value || isValidAddress(checkout.value.billingAddress))

  function resetState() {
    checkout.value = {}
    billingSameAsShipping.value = true
  }

  /**
   * Sync session shopping zip with shipping address zip
   * Shows notification when zip is updated
   * @param {string} shippingZip - The shipping address zip code
   */
  function syncShoppingZipWithShipping(shippingZip) {
    if (shippingZip && shippingZip !== useSessionStore().shoppingZip) {
      // setShoppingZip chains into updateUser(), which now throws on API error;
      // this is a fire-and-forget background sync, so swallow + log.
      useSessionStore()
        .setShoppingZip(shippingZip)
        .catch(error => {
          $rollbar.error('Failed to sync shopping zip with shipping', error)
        })
    }
  }

  /**
   * Parse the checkout response to align data structures
   * @param {Object} data - The checkout response
   */
  function parseCheckoutResponse(data) {
    const {
      billingFirstName,
      billingLastName,
      billingAddress1,
      billingAddress2,
      billingCity,
      billingCountry,
      billingState,
      billingZip,
      shippingFirstName,
      shippingLastName,
      shippingAddress1,
      shippingAddress2,
      shippingCity,
      shippingCountry,
      shippingState,
      shippingZip,
      items,
      ...rest
    } = data

    const billingAddress = {
      firstName: billingFirstName,
      lastName: billingLastName,
      address1: billingAddress1,
      address2: billingAddress2,
      city: billingCity,
      country: billingCountry,
      zip: billingZip,
      state: billingState,
    }

    const shippingAddress = {
      firstName: shippingFirstName,
      lastName: shippingLastName,
      address1: shippingAddress1,
      address2: shippingAddress2,
      city: shippingCity,
      country: shippingCountry,
      zip: shippingZip,
      state: shippingState,
    }

    // Determine if billing and shipping addresses are the same, for use in the UI
    billingSameAsShipping.value = isEqual(billingAddress, shippingAddress)

    const { cartItemGroups } = useCartStore()

    return {
      ...rest,
      items: items
        // align with cart items
        .map(item =>
          enrichAuctionItem({
            ...item,
            auctionItemId: item.itemId,
            isDeliveryFulfillment: item.fulfillmentMethod === DELIVERY,
            isPickupFulfillment: item.fulfillmentMethod === PICKUP,
          })
        )
        // force item order to match cart item groups
        .reduce((acc, item) => {
          Object.keys(cartItemGroups).forEach(key => {
            if (cartItemGroups[key].some(i => i.auctionItemId === item.auctionItemId)) {
              acc.push(item)
            }
          })

          return acc
        }, []),
      billingAddress,
      shippingAddress,
    }
  }

  /**
   * Initialize the checkout instance
   */
  function init() {
    const {
      user,
      userHasBusinessAccount,
      userShippingAddress,
      userBillingAddress,
      userBusinessShippingAddresses,
      userBusinessBillingAddresses,
    } = useUserStore()

    let defaultShippingAddress = userShippingAddress
    let defaultBillingAddress = userBillingAddress

    // For business accounts, use first business address if available, otherwise fall back to user address
    if (userHasBusinessAccount) {
      // Use first available business shipping address
      if (userBusinessShippingAddresses?.length > 0) {
        defaultShippingAddress = userBusinessShippingAddresses[0]
      }

      // Use first available business billing address
      if (userBusinessBillingAddresses?.length > 0) {
        defaultBillingAddress = userBusinessBillingAddresses[0]
      }
    }

    // use account shipping/billing for initial values
    const shippingAddress = {
      ...defaultShippingAddress,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
    }
    const billingAddress = {
      ...defaultBillingAddress,
      firstName: user?.firstName,
      lastName: user?.lastName,
    }

    return $pegApi.Checkout.create({
      items: useCartStore().cartSelectedItemIds,
      shippingAddress,
      billingAddress,
    })
      .then(resp => {
        checkout.value = parseCheckoutResponse(resp.checkout)

        // Sync shopping zip with shipping address zip
        syncShoppingZipWithShipping(shippingAddress.zip)

        $analytics.interaction('Started Checkout', {
          $value: checkout.value.totalPrice,
          ItemNames: checkout.value.items.map(item => item.title),
          CheckoutURL: `${window.location.origin}/checkout`,
          // Categories: ['Fiction', 'Children', 'Classics'],
          Items: checkout.value.items.map(item => ({
            ProductID: item.id,
            SKU: item.sku,
            ProductName: item.title,
            Quantity: item.quantity || 1,
            ItemPrice: item.auctionPurchasePrice,
            RowTotal: item.auctionPurchasePrice * (item.quantity || 1),
            ProductURL: item.fullProductUrl,
            ImageURL: item.images?.[0]?.url,
            // ProductCategories: item.categories,
          })),
        })

        return { success: true }
      })
      .catch(() => {
        return { success: false }
      })
  }

  /**
   * Update the checkout instance
   * @param {Object} data - The data to update (SAME AS INIT)
   */
  async function updateCheckout(data) {
    const { billingAddress, shippingAddress } = checkout.value
    isUpdating.value = true

    // IMPORTANT: billing and shiping addresses are required for update
    // PEG-826: $pegApi now throws on error. Address-selection callers
    // (CheckoutShippingAddress / CheckoutBillingAddress) read { success }
    // without their own try/catch, so keep this wrapper's { success } contract
    // by catching here — consistent with init() and placeOrder() in this store.
    try {
      const resp = await $pegApi.Checkout.update({
        billingAddress,
        shippingAddress,
        ...data,
      })
      checkout.value = parseCheckoutResponse(resp.checkout)

      return { success: true }
    } catch (error) {
      $rollbar.error('Checkout updateCheckout failed', error)
      return { success: false, message: error?.message }
    } finally {
      isUpdating.value = false
    }
  }

  function updateCheckoutBillingAddress(address) {
    const billingAddress = removeUnusedFields(address, ['id', 'addressType'])

    // TODO: temporarily inject user first/last name until we migrate to use `addresses` table for personal accounts
    if (!billingAddress.firstName) {
      billingAddress.firstName = useUserStore().user?.firstName
      billingAddress.lastName = useUserStore().user?.lastName
    }

    // Inject user email
    if (!billingAddress.email) {
      billingAddress.email = useUserStore().user?.email
    }

    return updateCheckout({ billingAddress })
  }

  async function updateCheckoutShippingAddress(address) {
    const shippingAddress = removeUnusedFields(address, ['id', 'addressType'])

    // TODO: temporarily inject user first/last name until we migrate to use `addresses` table for personal accounts
    if (!shippingAddress.firstName) {
      shippingAddress.firstName = useUserStore().user?.firstName
      shippingAddress.lastName = useUserStore().user?.lastName
    }

    // Inject user email
    if (!shippingAddress.email) {
      shippingAddress.email = useUserStore().user?.email
    }

    const result = await updateCheckout({ shippingAddress })
    // Sync shopping zip with updated shipping address zip. result.success is
    // updateCheckout's own synthesized flag (it catches the throw internally),
    // not a raw $pegApi return — kept intentionally per the PegForm-adjacent
    // { success } contract.
    if (result.success) {
      syncShoppingZipWithShipping(shippingAddress.zip)
    }
    return result
  }

  /**
   * Place the order
   * @param {Object} data - The payment data
   * @param {string} data.nonce - The payment nonce
   */
  async function placeOrder({ nonce }) {
    try {
      const { success, ...rest } = await $pegApi.Checkout.complete({ nonce })
      resetState()
      return { success, ...rest }
    } catch (error) {
      $rollbar.error('Checkout placeOrder failed', error)
      return { success: false, message: 'Payment Failed. Please double-check your payment info and try again.' }
    }
  }

  return {
    // STATE
    checkout,
    isUpdating,
    // GETTERS
    billingAddressIsComplete,
    billingSameAsShipping,
    hasError,
    shippingAddressIsComplete,
    hasDeliveryFulfillment,
    hasPickupFulfillment,
    // ACTIONS
    init,
    placeOrder,
    resetState,
    updateCheckout,
    updateCheckoutBillingAddress,
    updateCheckoutShippingAddress,
  }
})
